import { cn } from 'lib/utils'
import * as React from 'react'

// Types
export type TableProps = React.HTMLAttributes<HTMLTableElement>

export type TableHeaderProps = React.HTMLAttributes<HTMLTableSectionElement>

export type TableBodyProps = React.HTMLAttributes<HTMLTableSectionElement>

export type TableFooterProps = React.HTMLAttributes<HTMLTableSectionElement>

export type TableRowProps = React.HTMLAttributes<HTMLTableRowElement>

export type TableHeadProps = React.ThHTMLAttributes<HTMLTableCellElement>

export type TableCellProps = React.TdHTMLAttributes<HTMLTableCellElement>

export type TableCaptionProps = React.HTMLAttributes<HTMLTableCaptionElement>

// Components
const Table = React.forwardRef<HTMLTableElement, TableProps>(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto h-full">
    <table ref={ref} className={cn('w-full caption-bottom text-s-400', className)} {...props} />
  </div>
))
Table.displayName = 'Table'

const TableHeader = React.forwardRef<HTMLTableSectionElement, TableHeaderProps>(
  ({ className, ...props }, ref) => (
    <thead ref={ref} className={cn('[&_tr]:border-b', className)} {...props} />
  ),
)
TableHeader.displayName = 'TableHeader'

const TableBody = React.forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className, ...props }, ref) => (
    <tbody
      ref={ref}
      className={cn(
        // The name column reads as the row's link: it lifts to brand blue when
        // the row is hovered. Keyed on `.cursor-pointer` — the class DataTable
        // puts on every clickable row — so static tables (holidays, sandbox
        // numbers, debug logs) don't imply a click target they don't have.
        // Mono dates/IDs may be the first column too; their explicit compact
        // cell size must not be overridden by the name-column emphasis.
        '[&_tr>td:first-child:not(.font-jetbrains)]:text-s-500 [&_tr>td:first-child]:text-primary',
        '[&_tr.cursor-pointer>td:first-child]:transition-colors',
        '[&_tr.cursor-pointer:hover>td:first-child]:text-brand-text',
        className,
      )}
      {...props}
    />
  ),
)
TableBody.displayName = 'TableBody'

const TableFooter = React.forwardRef<HTMLTableSectionElement, TableFooterProps>(
  ({ className, ...props }, ref) => (
    <tfoot
      ref={ref}
      className={cn('border-t bg-muted/50 font-medium [&>tr]:last:border-b-0', className)}
      {...props}
    />
  ),
)
TableFooter.displayName = 'TableFooter'

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        'border-b transition-colors hover:bg-bg2 data-[state=selected]:bg-bg2',
        className,
      )}
      {...props}
    />
  ),
)
TableRow.displayName = 'TableRow'

const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        // Table headers stay mono and uppercase, but use tighter tracking and
        // the secondary token so dense data remains legible in both themes.
        // `font-medium` was stock Tailwind, which this repo's scale disallows —
        // weight now comes from the text-label token.
        'h-12 px-4 text-left align-middle font-jetbrains text-label uppercase tracking-[0.08em] text-secondary [&:has([role=checkbox])]:pr-0',
        className,
      )}
      {...props}
    />
  ),
)
TableHead.displayName = 'TableHead'

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, ...props }, ref) => (
    <td
      ref={ref}
      className={cn('p-4 align-middle [&:has([role=checkbox])]:pr-0', className)}
      {...props}
    />
  ),
)
TableCell.displayName = 'TableCell'

const TableCaption = React.forwardRef<HTMLTableCaptionElement, TableCaptionProps>(
  ({ className, ...props }, ref) => (
    <caption
      ref={ref}
      className={cn('mt-4 text-s-400 text-muted-foreground', className)}
      {...props}
    />
  ),
)
TableCaption.displayName = 'TableCaption'

export { Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow }
